/*
 * Backend hosts — the single source of truth shared by BOTH the app
 * (features/dashboard/config.ts) and the dev proxy (vite.config.ts).
 *
 * Clusters:
 *   br — Brazil report (world-cup-engagement) in prod
 *   eu — Spain + France reports, AND the unified auth endpoint
 *   ah — Albert Heijn campaign report (its own prod backend)
 * QA is one shared host for everything.
 *
 * Why a proxy at all? The prod backends answer CORS preflights with
 * `Access-Control-Allow-Methods: GET,OPTIONS` (no POST) and
 * `Access-Control-Allow-Headers: *` — and modern browsers do NOT let `*`
 * cover the `Authorization` header. So calling these hosts cross-origin from
 * the browser (login POST, or any GET carrying a bearer token) is blocked.
 * In dev we therefore route through a SAME-ORIGIN proxy, one path prefix per
 * cluster, so each report reaches its own backend without tripping CORS.
 */

export type DeployEnv = 'qa' | 'prod';
export type Cluster = 'br' | 'eu' | 'ah';

export const CLUSTER_HOST: Record<Cluster, Record<DeployEnv, string>> = {
  br: {
    qa: 'https://client-portal.qa.ca.blp.im',
    prod: 'https://client-portal.prod.br.blp.im',
  },
  // The general-purpose `client-portal` cluster — Spain + France live here
  // (shared backend; reports distinguished by report_id, not host).
  eu: {
    qa: 'https://client-portal.qa.ca.blp.im',
    prod: 'https://client-portal.mini-games.prod.eu.blp.im',
  },
  // Albert Heijn has its OWN prod backend; QA still shares the one host.
  ah: {
    qa: 'https://client-portal.qa.ca.blp.im',
    prod: 'https://client-portal.ah.prod.eu.blp.im',
  },
};

/** Unified auth always lives on the EU cluster (one login for every report). */
export const AUTH_CLUSTER: Cluster = 'eu';

/** Same-origin dev-proxy prefixes — see vite.config.ts. */
export const DEV_PROXY_PREFIX: Record<Cluster, string> = {
  br: '/be-br',
  eu: '/be-eu',
  ah: '/be-ah',
};
